/*
 * <scentt-popup> \u2013 sign-up offer dialog (sections/scentt-signup-popup.liquid).
 *
 * Data attributes (validated): data-delay (2\u201360 s), data-days (1\u201360), data-exit ("true"), data-version (int),
 * data-design-mode (present in the theme editor), data-section-id.
 *
 * Storage (all untrusted, shape-checked, failures ignored):
 *   localStorage  scentt:popup         {"v":<version>,"s":"closed"|"joined","t":<ms>}  \u2013 when to stay away
 *   sessionStorage scentt:popup-submit {"v":<version>,"t":<ms>}                         \u2013 one-time proof that this
 *                                                                                         tab submitted the form
 * The success view (and the optional code inside an inert <template>) is only shown when the submit flag is
 * present, matches the version and is less than 10 minutes old. Loading the page with ?customer_posted=true
 * without submitting shows nothing. If storage is blocked the success view is never shown (safe default).
 *
 * Runs before or after scentt-core.js (both deferred), so window.Scentt is only used lazily.
 */
(() => {
  if (customElements.get('scentt-popup')) return;

  const KEY = 'scentt:popup';
  const SUBMIT_KEY = 'scentt:popup-submit';
  const SUBMIT_TTL = 10 * 60 * 1000;
  const DAY = 864e5;
  const SKIP_PATHS = /\/(cart|checkout|checkouts|account|challenge|password)(\/|$)/;

  const int = (v, def, min, max) => {
    const n = Number.parseInt(v, 10);
    if (!Number.isFinite(n)) return def;
    return Math.min(max, Math.max(min, n));
  };
  const storage = (type) => {
    try {
      const s = window[type];
      const probe = '__scentt';
      s.setItem(probe, '1');
      s.removeItem(probe);
      return s;
    } catch (e) {
      return null;
    }
  };
  const readJSON = (store, key) => {
    if (!store) return null;
    let v = null;
    try {
      const raw = store.getItem(key);
      if (!raw || raw.length > 200) return null;
      try { v = JSON.parse(raw); } catch (err) { v = undefined; }
      if (v === undefined) throw new Error('bad json');
      return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
    } catch (e) {
      try { store.removeItem(key); } catch (err) { /* ignore */ }
      return null;
    }
  };
  const writeJSON = (store, key, value) => {
    if (!store) return;
    try { store.setItem(key, JSON.stringify(value)); } catch (e) { /* quota / blocked */ }
  };
  const reduced = () => Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  class ScenttPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.form = this.dialog.querySelector('form');
      this.version = int(this.dataset.version, 1, 0, 1e6);
      this.days = int(this.dataset.days, 14, 1, 60);
      this.delay = int(this.dataset.delay, 12, 2, 60) * 1000;
      this.designMode = this.hasAttribute('data-design-mode');
      this.local = storage('localStorage');
      this.session = storage('sessionStorage');
      this.done = false;

      this.onClick = this.onClick.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onCancel = () => { /* native Esc closes; 'close' handler does the rest */ };
      this.onExit = this.onExit.bind(this);
      this.onSelect = this.onSelect.bind(this);
      this.onDeselect = this.onDeselect.bind(this);

      this.dialog.addEventListener('click', this.onClick);
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('cancel', this.onCancel);
      if (this.form) this.form.addEventListener('submit', this.onSubmit);
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);

      if (this.handlePosted()) return;
      if (this.designMode) return;
      if (SKIP_PATHS.test(window.location.pathname)) return;
      if (this.seenRecently()) return;
      this.timer = setTimeout(() => this.open(), this.delay);
      if (this.dataset.exit === 'true' && window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        document.addEventListener('mouseout', this.onExit);
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.copyTimer);
      document.removeEventListener('mouseout', this.onExit);
      document.removeEventListener('shopify:section:select', this.onSelect);
      document.removeEventListener('shopify:section:deselect', this.onDeselect);
      if (!this.dialog) return;
      this.dialog.removeEventListener('click', this.onClick);
      this.dialog.removeEventListener('close', this.onClose);
      this.dialog.removeEventListener('cancel', this.onCancel);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      if (this.dialog.open) this.dialog.close();
      document.body.classList.remove('scentt-popup-open');
    }

    /* ---------- state ---------- */
    seenRecently() {
      const s = readJSON(this.local, KEY);
      if (!s || s.v !== this.version || typeof s.t !== 'number') return false;
      if (s.s === 'joined') return true;
      if (s.s !== 'closed') return false;
      const age = Date.now() - s.t;
      return age >= 0 && age < this.days * DAY;
    }

    stamp(state) {
      writeJSON(this.local, KEY, { v: this.version, s: state, t: Date.now() });
    }

    /** True when this tab really submitted the form (one-time flag, consumed here). */
    consumeSubmitFlag() {
      const f = readJSON(this.session, SUBMIT_KEY);
      if (this.session) {
        try { this.session.removeItem(SUBMIT_KEY); } catch (e) { /* ignore */ }
      }
      if (!f || f.v !== this.version || typeof f.t !== 'number') return false;
      const age = Date.now() - f.t;
      return age >= 0 && age < SUBMIT_TTL;
    }

    /** After a form post: show success / error only for a genuine submit from this tab. */
    handlePosted() {
      const doneEl = this.dialog.querySelector('[data-scentt-popup-done]');
      const errorEl = this.dialog.querySelector('[data-scentt-popup-error]');
      if (!doneEl && !errorEl) return false;
      const genuine = this.consumeSubmitFlag();
      this.cleanUrl();
      if (!genuine) return false;
      if (doneEl) {
        this.showDone(doneEl);
        this.stamp('joined');
      } else if (errorEl) {
        errorEl.hidden = false;
      }
      this.open();
      return true;
    }

    showDone(doneEl) {
      this.done = true;
      const fields = this.dialog.querySelector('[data-scentt-popup-fields]');
      if (fields) fields.hidden = true;
      const tpl = doneEl.querySelector('template[data-scentt-popup-code]');
      if (tpl && tpl.content) {
        const node = document.importNode(tpl.content, true);
        tpl.replaceWith(node);
      }
      doneEl.hidden = false;
      const title = doneEl.querySelector('[id]');
      if (title) this.dialog.setAttribute('aria-labelledby', title.id);
    }

    /** Drop ?customer_posted=\u2026 so a reload or a shared link does not re-trigger the success state. */
    cleanUrl() {
      try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('customer_posted')) return;
        url.searchParams.delete('customer_posted');
        url.hash = '';
        window.history.replaceState(window.history.state, '', url.pathname + url.search);
      } catch (e) { /* ignore */ }
    }

    /* ---------- open / close ---------- */
    open() {
      clearTimeout(this.timer);
      document.removeEventListener('mouseout', this.onExit);
      if (!this.dialog || this.dialog.open) return;
      // Never stack on top of another open modal (cart drawer, menu, quick add).
      if (!this.designMode && !this.done && document.querySelector('dialog[open]:not(.scentt-popup), .drawer.active, details.menu-opening')) {
        this.timer = setTimeout(() => this.open(), 5000);
        return;
      }
      this.returnFocus = document.activeElement;
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      this.dialog.classList.toggle('scentt-popup--still', reduced());
      document.body.classList.add('scentt-popup-open');
      const target = this.done
        ? this.dialog.querySelector('[data-scentt-popup-done] button')
        : this.dialog.querySelector('input[type="email"]');
      if (target && !window.matchMedia('(hover: none)').matches) {
        try { target.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
      }
    }

    close() {
      if (!this.dialog) return;
      if (this.dialog.open && typeof this.dialog.close === 'function') this.dialog.close();
      else if (this.dialog.hasAttribute('open')) {
        this.dialog.removeAttribute('open');
        this.onClose();
      }
    }

    onClose() {
      document.body.classList.remove('scentt-popup-open');
      if (!this.designMode && !this.done && !this.submitting) this.stamp('closed');
      const back = this.returnFocus;
      this.returnFocus = null;
      if (back && typeof back.focus === 'function' && document.contains(back)) {
        try { back.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
      }
    }

    onClick(e) {
      if (e.target === this.dialog) { this.close(); return; } // backdrop
      const t = e.target.closest ? e.target : null;
      if (!t) return;
      if (t.closest('[data-scentt-popup-close]')) { this.close(); return; }
      const copy = t.closest('[data-scentt-popup-copy]');
      if (copy) this.copy(copy);
    }

    onSubmit() {
      this.submitting = true;
      writeJSON(this.session, SUBMIT_KEY, { v: this.version, t: Date.now() });
    }

    onExit(e) {
      if (!e.relatedTarget && e.clientY < 10) this.open();
    }

    onSelect(e) {
      if (e.detail && e.detail.sectionId === this.dataset.sectionId) this.open();
    }

    onDeselect(e) {
      if (e.detail && e.detail.sectionId === this.dataset.sectionId) this.close();
    }

    copy(btn) {
      const valueEl = btn.querySelector('[data-code-value]');
      const label = btn.querySelector('[data-copy-label]');
      const code = valueEl ? valueEl.textContent.trim().slice(0, 64) : '';
      if (!code) return;
      const done = () => {
        if (!label) return;
        if (!this.copyLabel) this.copyLabel = label.textContent;
        label.textContent = String(btn.dataset.copied || this.copyLabel).slice(0, 40);
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => { label.textContent = this.copyLabel; }, 1600);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done, () => this.selectCode(valueEl));
      } else {
        this.selectCode(valueEl);
      }
    }

    selectCode(el) {
      if (!el) return;
      try {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } catch (e) { /* ignore */ }
    }
  }

  customElements.define('scentt-popup', ScenttPopup);
})();
